import React, { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { PROBES, Style } from "./probes";

/**
 * Remi Tool Coverage Check  ->  route /RemiCoverage
 *
 * Answers one question: "which billing endpoints can Remi actually reach, and
 * what shape do they come back in?" Run it with a real logged-in session and
 * hand the exported JSON to the assistant-microservice team - it tells them
 * exactly which tools are buildable today and which need attention first.
 *
 * Scope: reachability, auth, and response shape. It does NOT assert business
 * correctness - a 200 with the wrong rows still shows green here.
 */

const CONCURRENCY = 5;

function todayMinus(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

/**
 * The portal's endpoints frequently return a JSON *string* that has to be parsed
 * a second time. Detecting that here saves the backend team from discovering it
 * one endpoint at a time.
 */
/**
 * Replaces every scalar with its type, keeping structure. This is what the
 * backend team actually needs from a coverage report - field names and shape -
 * and it contains no patient data, so it is safe to paste into a ticket.
 */
function redactValues(value, depth = 0) {
  if (depth > 4) return "<...>";
  if (value === null) return null;
  if (Array.isArray(value)) {
    // One representative row is enough to convey shape.
    return value.length ? [redactValues(value[0], depth + 1), `<${value.length} rows>`] : [];
  }
  if (typeof value === "object") {
    const out = {};
    for (const [k, v] of Object.entries(value)) out[k] = redactValues(v, depth + 1);
    return out;
  }
  return `<${typeof value}>`;
}

/** Top-level field names of the first row, for the at-a-glance column. */
function fieldNamesOf(value) {
  if (Array.isArray(value)) {
    return value.length && value[0] && typeof value[0] === "object" ? Object.keys(value[0]) : [];
  }
  if (value && typeof value === "object") {
    const arrayKey = Object.keys(value).find((k) => Array.isArray(value[k]));
    if (arrayKey) {
      const rows = value[arrayKey];
      return rows.length && rows[0] && typeof rows[0] === "object"
        ? Object.keys(rows[0])
        : Object.keys(value);
    }
    return Object.keys(value);
  }
  return [];
}

function analyseBody(rawText) {
  const out = { parsed: null, doubleEncoded: false, rowCount: null, note: "" };
  if (!rawText) {
    out.note = "empty body";
    return out;
  }

  let first;
  try {
    first = JSON.parse(rawText);
  } catch {
    out.note = rawText.trim().startsWith("<") ? "HTML/XML, not JSON" : "unparseable";
    return out;
  }

  let value = first;
  if (typeof first === "string") {
    try {
      value = JSON.parse(first);
      out.doubleEncoded = true;
    } catch {
      value = first;
    }
  }

  out.parsed = value;

  if (Array.isArray(value)) out.rowCount = value.length;
  else if (value && typeof value === "object") {
    const arrayKey = Object.keys(value).find((k) => Array.isArray(value[k]));
    if (arrayKey) {
      out.rowCount = value[arrayKey].length;
      out.note = `rows in "${arrayKey}"`;
    }
  }
  return out;
}

async function runProbe(probe, params, token) {
  const missing = (probe.needs || []).filter((k) => !params[k]);
  if (missing.length) {
    return { status: "skipped", note: `needs ${missing.join(", ")}`, ms: 0 };
  }

  const base = probe.base();
  const inputJson = JSON.stringify(probe.input(params));
  const started = performance.now();

  let url;
  let options;

  if (probe.style === Style.Asmx) {
    // Legacy ASMX: POST, body is {"sInputJSON":"<stringified json>"}
    url = `${base}${probe.path}`;
    options = {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ sInputJSON: inputJson }),
    };
  } else {
    // Modern REST: GET with ?sInputJSON={...}
    url = `${base}${probe.path}?sInputJSON=${encodeURIComponent(inputJson)}`;
    options = {
      method: "GET",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    };
  }

  try {
    const res = await fetch(url, options);
    const ms = Math.round(performance.now() - started);
    const rawText = await res.text();
    const analysis = analyseBody(rawText);

    let status = "ok";
    if (res.status === 401 || res.status === 403) status = "auth";
    else if (!res.ok) status = "fail";
    else if (!analysis.parsed && analysis.note) status = "shape";

    return {
      status,
      httpStatus: res.status,
      ms,
      contentType: res.headers.get("content-type") || "",
      doubleEncoded: analysis.doubleEncoded,
      rowCount: analysis.rowCount,
      note: analysis.note,
      // Safe to export: structure only, every value replaced by its type.
      shape: analysis.parsed ? redactValues(analysis.parsed) : null,
      fields: analysis.parsed ? fieldNamesOf(analysis.parsed) : [],
      // PHI. On-screen reveal only, behind an explicit click. NEVER exported.
      _liveSample: rawText.slice(0, 400),
      _liveUrl: url,
    };
  } catch (err) {
    return {
      status: "unreachable",
      ms: Math.round(performance.now() - started),
      note: err.message === "Failed to fetch" ? "network/CORS/mixed-content" : err.message,
      url,
    };
  }
}

const STATUS_STYLE = {
  ok: { bg: "#e6f7f2", fg: "#0e6b60", label: "READY" },
  shape: { bg: "#fff6e5", fg: "#8a5a00", label: "SHAPE?" },
  fail: { bg: "#fdeaea", fg: "#a12a2a", label: "FAIL" },
  auth: { bg: "#fdeaea", fg: "#a12a2a", label: "AUTH" },
  unreachable: { bg: "#fdeaea", fg: "#a12a2a", label: "UNREACH" },
  skipped: { bg: "#eef1f2", fg: "#65757c", label: "SKIP" },
  running: { bg: "#e8f1fb", fg: "#1c5aa8", label: "..." },
};

function Badge({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.skipped;
  return (
    <span
      style={{
        background: s.bg, color: s.fg, fontWeight: 700, fontSize: 11,
        padding: "2px 7px", borderRadius: 4, letterSpacing: 0.3, whiteSpace: "nowrap",
      }}
    >
      {s.label}
    </span>
  );
}

export default function CoverageCheck() {
  const userData = useSelector((state) => state.user.user);
  const token = useSelector((state) => state.user.token);
  const reduxClaimId = useSelector((state) => state.user.claimId);

  const [params, setParams] = useState({
    practiceId: userData?.practiceID ?? "",
    claimId: reduxClaimId ?? "",
    patientId: "",
    searchText: "a",
    fromDate: todayMinus(30),
    toDate: todayMinus(0),
  });

  const [results, setResults] = useState({});
  const [running, setRunning] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [revealed, setRevealed] = useState({}); // per-row opt-in to see live PHI
  const [copied, setCopied] = useState(false);

  const setParam = (k) => (e) => setParams((p) => ({ ...p, [k]: e.target.value }));

  const runAll = useCallback(async () => {
    setRunning(true);
    setResults({});

    const queue = [...PROBES.entries()];
    const workers = Array.from({ length: CONCURRENCY }, async () => {
      for (;;) {
        const next = queue.shift();
        if (!next) return;
        const [index, probe] = next;
        setResults((r) => ({ ...r, [index]: { status: "running" } }));
        const result = await runProbe(probe, params, token);
        setResults((r) => ({ ...r, [index]: result }));
      }
    });

    await Promise.all(workers);
    setRunning(false);
  }, [params, token]);

  const summary = useMemo(() => {
    const counts = { ok: 0, shape: 0, fail: 0, auth: 0, unreachable: 0, skipped: 0 };
    Object.values(results).forEach((r) => {
      if (r.status in counts) counts[r.status] += 1;
    });
    return counts;
  }, [results]);

  /**
   * Exported report is PHI-free by construction: any key starting with "_" is
   * dropped, and only redacted shape/field-names survive. This is meant to be
   * pasted into a ticket or emailed to the backend team.
   */
  const copyReport = () => {
    const report = {
      generatedAt: new Date().toISOString(),
      note: "Structure only - all values redacted. Contains no patient data.",
      summary,
      probes: PROBES.map((p, i) => {
        const r = results[i] || { status: "not run" };
        const { _liveSample, _liveUrl, ...safe } = r;
        return {
          group: p.group,
          tool: p.tool,
          endpoint: `${p.base()}${p.path}`,
          style: p.style,
          ...safe,
        };
      }),
    };
    navigator.clipboard?.writeText(JSON.stringify(report, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const doneCount = Object.values(results).filter((r) => r.status !== "running").length;

  const cell = { padding: "7px 9px", borderBottom: "1px solid #eef1f2", verticalAlign: "top" };
  const inputStyle = {
    padding: "6px 9px", border: "1px solid #dfe6e8", borderRadius: 6,
    fontFamily: "inherit", fontSize: 13, width: 130,
  };

  return (
    <div style={{ padding: 24, fontFamily: "system-ui, Segoe UI, sans-serif", fontSize: 14, maxWidth: 1200, margin: "0 auto" }}>
      <h1 style={{ fontSize: 22, marginBottom: 4 }}>Remi — Tool Coverage Check</h1>
      <p style={{ color: "#64757c", marginTop: 0, marginBottom: 18 }}>
        Probes every billing endpoint behind a Remi tool using your current session token.
        Tests reachability, auth and response shape — not business correctness.
      </p>

      {!token && (
        <div style={{ background: "#fdeaea", color: "#a12a2a", padding: 12, borderRadius: 8, marginBottom: 16 }}>
          No auth token in session. Sign in to the portal first, then reload this page.
        </div>
      )}

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 16 }}>
        {[
          ["practiceId", "Practice ID"],
          ["claimId", "Claim ID"],
          ["patientId", "Patient ID"],
          ["searchText", "Search text"],
          ["fromDate", "From"],
          ["toDate", "To"],
        ].map(([key, label]) => (
          <label key={key} style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 12, color: "#64757c" }}>
            {label}
            <input style={inputStyle} value={params[key]} onChange={setParam(key)} />
          </label>
        ))}

        <button
          onClick={runAll}
          disabled={running || !token}
          style={{
            padding: "9px 20px", border: "none", borderRadius: 7, cursor: running ? "wait" : "pointer",
            background: "linear-gradient(135deg,#23c2af,#0e6b60)", color: "#fff", fontWeight: 600, fontSize: 14,
          }}
        >
          {running ? `Running ${doneCount}/${PROBES.length}...` : `Run all ${PROBES.length} probes`}
        </button>

        <button
          onClick={copyReport}
          disabled={!doneCount}
          style={{
            padding: "9px 16px", border: "1px solid #dfe6e8", borderRadius: 7,
            cursor: "pointer", background: "#fff", fontSize: 13,
          }}
        >
          {copied ? "Copied — redacted" : "Copy JSON report (redacted)"}
        </button>
      </div>

      {doneCount > 0 && (
        <div style={{ display: "flex", gap: 14, marginBottom: 14, flexWrap: "wrap" }}>
          {Object.entries(summary).map(([k, v]) =>
            v ? (
              <span key={k} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <Badge status={k} />
                <strong>{v}</strong>
              </span>
            ) : null
          )}
          <span style={{ color: "#64757c" }}>
            of {PROBES.length} — <strong>{summary.ok}</strong> tools buildable today
          </span>
        </div>
      )}

      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ textAlign: "left", color: "#64757c", fontSize: 11, textTransform: "uppercase" }}>
            <th style={cell}>Status</th>
            <th style={cell}>Tool</th>
            <th style={cell}>Endpoint</th>
            <th style={cell}>Style</th>
            <th style={cell}>HTTP</th>
            <th style={cell}>ms</th>
            <th style={cell}>Rows</th>
            <th style={cell}>Notes</th>
          </tr>
        </thead>
        <tbody>
          {PROBES.map((probe, i) => {
            const r = results[i];
            return (
              <React.Fragment key={probe.tool}>
                <tr
                  onClick={() => setExpanded(expanded === i ? null : i)}
                  style={{ cursor: r?.shape ? "pointer" : "default" }}
                >
                  <td style={cell}><Badge status={r?.status || "skipped"} /></td>
                  <td style={{ ...cell, fontFamily: "ui-monospace, monospace", fontWeight: 600 }}>{probe.tool}</td>
                  <td style={{ ...cell, fontFamily: "ui-monospace, monospace", color: "#64757c", fontSize: 12 }}>{probe.path}</td>
                  <td style={cell}>{probe.style}</td>
                  <td style={cell}>{r?.httpStatus ?? "—"}</td>
                  <td style={cell}>{r?.ms ?? "—"}</td>
                  <td style={cell}>{r?.rowCount ?? "—"}</td>
                  <td style={{ ...cell, color: "#64757c" }}>
                    {r?.doubleEncoded && (
                      <span style={{ color: "#8a5a00", fontWeight: 600, marginRight: 6 }}>double-encoded</span>
                    )}
                    {r?.note}
                  </td>
                </tr>
                {expanded === i && r?.shape && (
                  <tr>
                    <td colSpan={8} style={{ ...cell, background: "#f7f9fa" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 7 }}>
                        <strong style={{ fontSize: 12 }}>
                          {revealed[i] ? "Live response (contains patient data)" : "Response shape — values redacted"}
                        </strong>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setRevealed((v) => ({ ...v, [i]: !v[i] }));
                          }}
                          style={{
                            padding: "3px 9px", fontSize: 11, borderRadius: 5, cursor: "pointer",
                            border: "1px solid #dfe6e8",
                            background: revealed[i] ? "#fdeaea" : "#fff",
                            color: revealed[i] ? "#a12a2a" : "#64757c",
                          }}
                        >
                          {revealed[i] ? "Hide live values" : "Reveal live values"}
                        </button>
                        {revealed[i] && (
                          <span style={{ fontSize: 11, color: "#a12a2a" }}>
                            Do not copy or paste this anywhere.
                          </span>
                        )}
                      </div>

                      {r.fields?.length > 0 && (
                        <div style={{ fontSize: 11, color: "#64757c", marginBottom: 6 }}>
                          <strong>{r.fields.length} fields:</strong> {r.fields.join(", ")}
                        </div>
                      )}

                      <pre style={{ margin: 0, fontSize: 11.5, whiteSpace: "pre-wrap", wordBreak: "break-all", maxHeight: 220, overflow: "auto" }}>
                        {revealed[i] ? r._liveSample : JSON.stringify(r.shape, null, 2)}
                      </pre>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      <div style={{ background: "#fff6e5", border: "1px solid #f0dcb4", color: "#8a5a00", padding: "10px 12px", borderRadius: 8, marginTop: 18, fontSize: 12.5 }}>
        <strong>This page calls live APIs and returns real patient data.</strong> Responses are
        shown redacted by default and the exported report contains structure only — no values, no
        URLs, no identifiers. Use “Reveal live values” only when you need it, and don’t paste
        revealed output anywhere.
      </div>

      <p style={{ color: "#64757c", fontSize: 12, marginTop: 12 }}>
        <strong>SKIP</strong> means a required test ID wasn't supplied — not a failure.
        <strong> SHAPE?</strong> means it responded 200 but the body didn't parse as usable JSON.
        <strong> double-encoded</strong> means the body is a JSON string containing JSON — the
        assistant service must normalise this once, centrally.
      </p>
    </div>
  );
}
